import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { plural } from "@/lib/plural";

/** Admin: issued certificates without Report/Certificate in the repository (Blocco 3.2). */
export default function MissingDocumentsPanel({ onRegenerated }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/missing-documents"); setItems(data); } catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function regenerate(list) {
    for (const it of list) {
      setBusy(it.id);
      try {
        const { data } = await api.post(`/assessments/${it.id}/regenerate-missing-documents`);
        toast.success(`${it.organization_name}: generati ${data.generated.join(" e ") || "nessun documento"}`);
      } catch (e) {
        toast.error(`${it.organization_name}: ${formatApiErrorDetail(e.response?.data?.detail)}`);
      }
    }
    setBusy(null);
    await load();
    onRegenerated?.();
  }

  if (!items || items.length === 0) return null;
  return (
    <Card className="rounded-2xl border border-amber-300 bg-amber-50 p-5 mb-6" data-testid="missing-docs-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-700" />
        <div className="flex-1">
          <div className="font-semibold text-amber-900 text-sm">
            {plural(items.length, "certificato emesso", "certificati emessi")} senza documenti nel repository
          </div>
          <div className="text-xs text-amber-800">La rigenerazione legge i dati del certificato senza modificarli (rating, banda, date, codice).</div>
        </div>
        <Button size="sm" disabled={!!busy} onClick={() => regenerate(items)} data-testid="regenerate-all-missing"
                className="bg-amber-600 hover:bg-amber-700 text-white">
          <RefreshCw className="w-4 h-4 mr-1" /> Rigenera tutti
        </Button>
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg border border-amber-200 px-3 py-2 text-sm" data-testid={`missing-docs-row-${it.id}`}>
            <span>
              <b>{it.organization_name}</b> · {it.certificate_code} · anno {it.year}
              <span className="ml-2 text-xs text-amber-700">Mancano: {it.missing.join(", ")}</span>
            </span>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => regenerate([it])} data-testid={`regenerate-missing-${it.id}`}>
              {busy === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rigenera documenti mancanti"}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
